import { DoctorInfo, RxDetails } from '../../../tasks/types/rx';

export type Severity = 'HIGH' | 'MEDIUM' | 'LOW';

export interface FillRecord {
  date: string;
  quantity: number;
  days: number;
}

export interface PhysicianBlock {
  doctor: DoctorInfo;
  fills: FillRecord[];
}

export interface RxGroup {
  drugName: string;
  severity: Severity;
  totalFills: number;
  latestFillDate: string;
  earliestFillDate: string;
  isSingleFill: boolean;
  quantity: number | null;
  days: number | null;
  varies: boolean;
  indications: string[];
  physicians: PhysicianBlock[];
}

const normalizeSeverity = (s: string): Severity => {
  const upper = (s || '').toUpperCase();
  if (upper === 'HIGH' || upper === 'MEDIUM' || upper === 'LOW') return upper;
  return 'LOW';
};

const groupKey = (drugName: string, severity: Severity) =>
  `${drugName}|${severity}`;

const physicianKey = (doctor: DoctorInfo) =>
  `${doctor?.name || ''}|${doctor?.address || ''}`;

const dedupeOrdered = (items: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    if (!seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
};

export const groupByDrugAndSeverity = (rx: RxDetails[]): RxGroup[] => {
  const groups = new Map<string, RxGroup>();
  const physicianMaps = new Map<string, Map<string, PhysicianBlock>>();

  for (const item of rx) {
    const severity = normalizeSeverity(item.severity);
    const key = groupKey(item.drugName, severity);

    let group = groups.get(key);
    if (!group) {
      group = {
        drugName: item.drugName,
        severity,
        totalFills: 0,
        latestFillDate: '',
        earliestFillDate: '',
        isSingleFill: false,
        quantity: item.quantity,
        days: item.days,
        varies: false,
        indications: [],
        physicians: [],
      };
      groups.set(key, group);
      physicianMaps.set(key, new Map());
    }

    if (group.quantity !== item.quantity || group.days !== item.days) {
      group.varies = true;
    }

    group.indications = dedupeOrdered([
      ...group.indications,
      ...item.indication,
    ]);

    const pMap = physicianMaps.get(key)!;
    const pKey = physicianKey(item.doctor);
    let block = pMap.get(pKey);
    if (!block) {
      block = { doctor: item.doctor, fills: [] };
      pMap.set(pKey, block);
    }
    for (const date of item.fillDates) {
      block.fills.push({ date, quantity: item.quantity, days: item.days });
    }
  }

  const result: RxGroup[] = [];
  for (const [key, group] of groups) {
    const pMap = physicianMaps.get(key)!;
    const physicians = Array.from(pMap.values()).map(block => ({
      ...block,
      fills: [...block.fills].sort((a, b) => b.date.localeCompare(a.date)),
    }));
    physicians.sort((a, b) => {
      const aLatest = a.fills[0]?.date || '';
      const bLatest = b.fills[0]?.date || '';
      return bLatest.localeCompare(aLatest);
    });

    const allDates: string[] = [];
    for (const p of physicians) for (const f of p.fills) allDates.push(f.date);
    allDates.sort();

    group.totalFills = allDates.length;
    group.earliestFillDate = allDates[0] || '';
    group.latestFillDate = allDates[allDates.length - 1] || '';
    group.isSingleFill = group.totalFills === 1;
    if (group.varies) {
      group.quantity = null;
      group.days = null;
    }
    group.physicians = physicians;
    result.push(group);
  }

  return result;
};

const SEVERITY_ORDER: Record<Severity, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };

export type SortColumn = 'severity' | 'date' | 'indication';
export type SortDirection = 'asc' | 'desc';

export const sortGroups = (
  groups: RxGroup[],
  column: SortColumn,
  direction: SortDirection,
): RxGroup[] => {
  const sign = direction === 'asc' ? 1 : -1;
  const copy = [...groups];
  copy.sort((a, b) => {
    if (column === 'severity') {
      return sign * (SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
    }
    if (column === 'date') {
      return sign * a.latestFillDate.localeCompare(b.latestFillDate);
    }
    const aInd = (a.indications[0] || '').toLowerCase();
    const bInd = (b.indications[0] || '').toLowerCase();
    return sign * aInd.localeCompare(bInd);
  });
  return copy;
};

export const countBySeverity = (
  groups: RxGroup[],
): { all: number; high: number; medium: number; low: number } => {
  const result = { all: groups.length, high: 0, medium: 0, low: 0 };
  for (const g of groups) {
    if (g.severity === 'HIGH') result.high += 1;
    else if (g.severity === 'MEDIUM') result.medium += 1;
    else result.low += 1;
  }
  return result;
};
